import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ClerkProvider, SignIn, SignedIn, SignedOut, UserButton, useAuth } from '@clerk/clerk-react';

import {
  rubricFullscreenToggled,
  rubricSaved,
  rubricItemSelected,
  rubricLibraryLoaded,
  rubricPageViewed,
  rubricPasted,
  rubricSampleLoaded,
  rubricScoreFailed,
  rubricScored,
  type AnalyticsEvent,
} from '../core/events';
import { validateScoreRequest, type Rubric } from '../core/rubric';
import { isLibraryRubric, libraryRubric } from '../core/library';
import { EXAMPLES, type ExampleId } from '../core/sample';
import { createAnalytics, nullAnalytics, stopAnalytics, swallow, type Analytics } from './analytics/client';
import { call, score as scoreCall, structureRubric, type GetToken, type ScoreResponse } from './api';
import type { PasteResult } from '../core/paste';
import AccessDenied from './components/AccessDenied';
import RubricHeader from './components/RubricHeader';
import Results from './components/Results';
import RubricPane from './components/RubricPane';
import TextPane from './components/TextPane';
import { GRADER } from './grader';
import { loadMine, loadRubric, saveMine, saveRubric } from './storage';

type Stage = 'loading' | 'denied' | 'ready';
type Mode = 'edit' | 'scoring' | 'results' | 'error';

export interface Auth {
  getToken: () => Promise<string | null>;
  userId: string | null;
}

export interface AppProps {
  auth: Auth;
  headerAction?: ReactNode;
  /** Injected in tests. In the browser the real adapter is built after sign-in. */
  analytics?: Analytics;
  /** Injected in tests so ids can be asserted on. */
  newId?: () => string;
  /** Injected in tests so `confirm` does not block. */
  confirmReplace?: () => boolean;
}

const LIMIT_MESSAGES: Record<string, (limit?: number) => string> = {
  text_empty: () => 'Paste the writing you want to score.',
  text_too_long: (limit) => `That is over the ${limit?.toLocaleString() ?? ''} character limit. Score a shorter piece.`,
  too_many_sentences: (limit) => `That is over ${limit ?? ''} sentences. Score a shorter piece.`,
  too_many_items: (limit) => `A rubric can have at most ${limit ?? ''} items.`,
  no_items: () => 'Add at least one rubric item.',
  item_name_empty: () => 'Every rubric item needs a name.',
  item_text_too_long: (limit) => `A rubric text is over ${limit?.toLocaleString() ?? ''} characters. Shorten it.`,
  title_too_long: (limit) => `The title is over ${limit ?? ''} characters.`,
  bad_level_count: () => 'Every item needs between two and six levels.',
  bad_pass_level: () => 'The pass level has to be one of the levels.',
};

/** The tool's name is in the bar above; the page leads with what it does. */
function Intro() {
  return (
    <div className="intro">
      <p className="eyebrow">Grade against a rubric</p>
      <h1>See which sentences earned each rating.</h1>
      <p className="tagline">Paste the writing, pick a rubric or bring your own, and every criterion comes back with its level and the sentences behind it.</p>
    </div>
  );
}

export function RubricApp({ auth, headerAction, analytics: injected, newId = () => crypto.randomUUID(), confirmReplace }: AppProps) {
  const { getToken, userId } = auth;
  const [analytics, setAnalytics] = useState<Analytics | null>(injected ?? null);
  const [stage, setStage] = useState<Stage>('loading');
  const [deniedReason, setDeniedReason] = useState<string | undefined>();

  const [mode, setMode] = useState<Mode>('edit');
  const [text, setText] = useState('');
  const examples = EXAMPLES;
  const [exampleId, setExampleId] = useState<ExampleId | ''>('');
  const [rubric, setRubric] = useState<Rubric>(() => loadRubric());
  // The teacher's own rubric survives switching to a template, so switching never asks.
  const [mine, setMine] = useState<Rubric | null>(() => (isLibraryRubric(rubric) ? loadMine() : rubric));
  const [result, setResult] = useState<ScoreResponse | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [evidenceCutoff, setEvidenceCutoff] = useState(50);
  const [fullscreen, setFullscreen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiStructure, setAiStructure] = useState(false);

  const track = useCallback((event: AnalyticsEvent) => {
    swallow('track', () => analytics?.track(event));
  }, [analytics]);

  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const api: GetToken = useCallback(() => tokenRef.current(), []);

  useEffect(() => {
    if (injected) return;
    let live = true;
    createAnalytics(userId ?? undefined)
      .then((a) => live && setAnalytics(a))
      .catch(() => live && setAnalytics(nullAnalytics()));
    return () => { live = false; stopAnalytics(); };
  }, [injected, userId]);

  useEffect(() => {
    let live = true;
    (async () => {
      const me = await call<{ userId: string; email: string | null; aiStructure?: boolean }>(api, '/api/me');
      if (!live) return;
      if (!me.ok) {
        setDeniedReason(me.status === 403 ? me.reason : me.status === 401 ? 'session_invalid' : 'unreachable');
        setStage('denied');
        return;
      }
      setAiStructure(me.data.aiStructure === true);
      setStage('ready');
    })();
    return () => { live = false; };
  }, [api]);

  const viewed = useRef(false);
  useEffect(() => {
    if (!analytics || viewed.current) return;
    viewed.current = true;
    track(rubricPageViewed());
  }, [analytics, track]);

  useEffect(() => { saveRubric(rubric); }, [rubric]);
  useEffect(() => { if (!isLibraryRubric(rubric)) setMine(rubric); }, [rubric]);
  useEffect(() => { if (mine) saveMine(mine); }, [mine]);

  const confirm = (message: string) => (confirmReplace ?? (() => window.confirm(message)))();

  const loadExample = (id: ExampleId) => {
    const example = examples.find((candidate) => candidate.id === id);
    const base = example && libraryRubric(example.rubricId);
    if (!example || !base) return;
    const exampleRubric = example.assignment ? { ...base, assignment: example.assignment } : base;
    // The rubric is safe under Yours; only writing the teacher typed is worth asking about.
    const replace = text.trim().length === 0 || exampleId !== '' || confirm('Replace your writing with this example?');
    if (!replace) return;
    setExampleId(id);
    setText(example.text);
    setRubric(exampleRubric);
    setResult(null);
    setSelectedItemId(null);
    setNotice(null);
    track(rubricSampleLoaded(id, example.rubricId));
  };

  /** Swap the rubric and keep the writing. */
  const loadLibraryRubric = (rubricId: string) => {
    const next = libraryRubric(rubricId);
    if (!next) return;
    setRubric(next);
    setResult(null);
    setSelectedItemId(null);
    track(rubricLibraryLoaded(rubricId));
  };

  /** Back to the teacher's own rubric, kept under Yours. */
  const loadMineRubric = () => {
    if (!mine) return;
    setRubric(mine);
    setResult(null);
    setSelectedItemId(null);
  };

  /** Leave results for the editor; the writing, rubric and last result stay. */
  const backToEditing = () => { setMode('edit'); setSelectedItemId(null); };
  const editRubric = () => { backToEditing(); toggleFullscreen(); };

  const toggleFullscreen = () => {
    const next = !fullscreen;
    setFullscreen(next);
    track(rubricFullscreenToggled(next));
  };

  const runScore = async () => {
    setNotice(null);
    setError(null);
    setMode('scoring');
    setResult(null);
    setSelectedItemId(null);
    const startedAt = performance.now();
    const outcome = await scoreCall(api, { text, rubric });
    if (!outcome.ok) {
      if (outcome.status === 422) {
        setNotice((LIMIT_MESSAGES[outcome.error] ?? (() => 'That could not be scored as written.'))(outcome.limit));
        setMode('edit');
      } else {
        setError(
          outcome.status === 429
            ? 'You have scored a lot in the last minute. Wait a moment and try again.'
            : outcome.status === 502
              ? outcome.upstreamStatus
                ? `The ${GRADER} returned an error (HTTP ${outcome.upstreamStatus}). Your writing and rubric are still here. You can try again.`
                : 'Scoring is not available right now. Nothing is wrong with your writing — try again in a moment.'
              : 'Something went wrong on our end. Your writing and rubric are still here — try again.',
        );
        setMode('error');
      }
      track(rubricScoreFailed(outcome.error));
      return;
    }
    setResult(outcome.data);
    setSelectedItemId(null);
    setFullscreen(false);
    setMode('results');
    track(rubricScored({
      itemCount: outcome.data.itemCount,
      sentenceCount: outcome.data.sentences.length,
      provider: outcome.data.provider,
      passedCount: outcome.data.passedCount,
      durationMs: Math.round(performance.now() - startedAt),
    }));
  };

  const structure = async (source: string): Promise<PasteResult | string> => {
    const outcome = await structureRubric(api, source);
    if (outcome.ok) return { ok: true, rubric: outcome.data.rubric, warnings: outcome.data.warnings };
    if (outcome.status === 422 && ['not_a_table', 'too_few_levels', 'too_many_levels'].includes(outcome.error)) {
      return { ok: false, reason: outcome.error as 'not_a_table' };
    }
    if (outcome.status === 429) return 'You have used this a lot in the last minute. Wait a moment and try again.';
    if (outcome.status === 422 && outcome.error === 'text_too_long') return 'That is too long to read. Paste just the rubric.';
    return 'AI could not read that right now. Try again, or paste the rubric as a table.';
  };

  const select = (itemId: string | null) => {
    setSelectedItemId(itemId);
    if (!result) return;
    const index = result.items.findIndex((i) => i.id === (itemId ?? selectedItemId));
    const item = result.items[index];
    if (item) track(rubricItemSelected(index, item.evidence.length, itemId !== null));
  };

  const highlights = new Map<string, number>();
  if (result && selectedItemId) {
    const item = result.items.find((i) => i.id === selectedItemId);
    if (item?.applies) for (const e of item.evidence) {
      if (e.probability >= evidenceCutoff / 100) highlights.set(e.sentenceId, e.probability);
    }
  }

  // The server's own check, run as the teacher types, so a disabled Score always says why.
  const validation = useMemo(() => validateScoreRequest({ text, rubric }), [text, rubric]);
  const blocker = validation.ok ? null : (LIMIT_MESSAGES[validation.error] ?? (() => 'That cannot be scored as written.'))(validation.limit);
  const canScore = mode === 'edit' && validation.ok;

  return (
    <>
      <RubricHeader headerAction={headerAction} />

    <main className={fullscreen ? 'page rubric-fullscreen' : 'page'}>
      <h1 className="visually-hidden">Rubric Score</h1>

      {stage === 'loading' && <p className="hint" role="status">Checking your access…</p>}
      {stage === 'denied' && <AccessDenied {...(deniedReason ? { reason: deniedReason } : {})} />}

      {stage === 'ready' && (
        <>
          {notice && <p className="message" data-tone="info" role="status">{notice}</p>}

          {mode === 'error' && (
            <div className="notice" role="alert">
              <h2>Scoring did not finish.</h2>
              <p>{error}</p>
              <button type="button" onClick={runScore}>Try again</button>
              <button type="button" className="secondary" onClick={() => { setMode('edit'); setError(null); }}>Back to editing</button>
            </div>
          )}

          {mode === 'results' && (
            // One way back, pinned to the top so it stays in reach while reading results.
            <div className="results-bar">
              <button type="button" className="secondary" onClick={backToEditing}>← Back to editing</button>
              <span className="hint"><span className="ph-mask">Graded with {rubric.title || 'your rubric'}</span>{result && ` · ${result.sentences.length} sentences. Select a criterion to see its evidence.`}</span>
            </div>
          )}

          {mode === 'results' && result ? (
            <>
              <div className="panes">
                <TextPane mode="results" text={text} sentences={result.sentences} highlights={highlights} />
                <Results
                  result={result}
                  rubric={rubric}
                  evidenceCutoff={evidenceCutoff}
                  onEvidenceCutoffChange={setEvidenceCutoff}
                  selectedItemId={selectedItemId}
                  onSelect={select}
                  onEdit={editRubric}
                />
              </div>
            </>
          ) : (
            // Writing first: the rubric is one strip, the writing gets the page.
            <div className="workspace">
              <RubricPane rubric={rubric} mine={mine} onLoadMine={loadMineRubric} onLoadLibrary={loadLibraryRubric} {...(aiStructure ? { onStructure: structure } : {})} onPasted={(source, parsed) => track(rubricPasted(parsed.ok
                ? { source, ok: true, itemCount: parsed.rubric.items.length, levelCount: parsed.rubric.items[0]?.levels.length ?? 0 }
                : { source, ok: false, reason: parsed.reason }))} onChange={(next) => { setRubric(next); track(rubricSaved(next.items.length, next.items[0]?.levels.length ?? 0)); }} disabled={mode !== 'edit'} fullscreen={fullscreen} onToggleFullscreen={toggleFullscreen} newId={newId}
                scoreAction={
                  <button
                    type="button"
                    className="primary score-button"
                    onClick={runScore}
                    disabled={!canScore}
                    aria-busy={mode === 'scoring'}
                    {...(mode === 'edit' && blocker ? { 'aria-describedby': 'score-blocker' } : {})}
                  >
                    {mode === 'scoring' ? 'Scoring…' : 'Score'}
                  </button>
                } />
              <TextPane mode="edit" text={text} onChange={(next) => { setText(next); setExampleId(''); }} examples={examples} exampleId={exampleId} onPickExample={loadExample} disabled={mode !== 'edit'}
                footNote={<>
                  {mode === 'scoring' && <span className="hint" role="status">Grading every sentence against the rubric…</span>}
                  {mode === 'edit' && blocker && <span className="hint score-blocker" id="score-blocker">{blocker}</span>}
                </>} />
            </div>
          )}

        </>
      )}
    </main>
    </>
  );
}

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * The browser-test identity. When set, it wins — including over a compiled-in publishable
 * key. Not the security boundary: the Worker decides that, and its bypass is honoured only
 * when no Clerk configuration exists. See packages/objectives/src/App.tsx for the long form.
 */
const e2eIdentity = import.meta.env.VITE_E2E_IDENTITY;

function ClerkAuthed() {
  const { getToken, userId } = useAuth();
  return <RubricApp auth={{ getToken, userId: userId ?? null }} headerAction={<UserButton />} />;
}

export default function App() {
  if (e2eIdentity) {
    return (
      <>
        <div className="test-identity-banner" role="status">
          Test identity build — sign-in is bypassed as <code>{e2eIdentity.split(':')[0]}</code>. Not for production.
        </div>
        <RubricApp auth={{ getToken: async () => e2eIdentity, userId: e2eIdentity.split(':')[0] || null }} />
      </>
    );
  }

  if (!publishableKey) {
    return (
      <>
      <RubricHeader />
      <main className="page">
        <div className="notice" role="status">
          <h2>Sign-in is not configured.</h2>
          <p>VITE_CLERK_PUBLISHABLE_KEY was not set when this bundle was built. See packages/rubric/README.md.</p>
        </div>
      </main>
      </>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInForceRedirectUrl="/"
      signUpForceRedirectUrl="/"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/"
    >
      <SignedOut>
        <RubricHeader />
        <main className="page centered">
          <Intro />
          <SignIn routing="hash" />
        </main>
      </SignedOut>
      <SignedIn>
        <ClerkAuthed />
      </SignedIn>
    </ClerkProvider>
  );
}
